import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { get } from 'lodash';
import { UploadedImageShot } from 'src/app/components/photographer/models/new/imageShot';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Selectable } from 'src/app/shared/types/selectable.type';

@Component({
	selector: 'bt-upload-multiple-modal',
	standalone: false,
	templateUrl: './upload-multiple-modal.component.html',
	styleUrl: './upload-multiple-modal.component.scss'
})
export class UploadMultipleModalComponent {
	uploadedImages: Selectable<UploadedImageShot>[] = [];
	readonly #dialogRef = inject(MatDialogRef<UploadMultipleModalComponent>);
	readonly data = inject<any>(MAT_DIALOG_DATA);
	loading = false;
	isDragging = false;

	constructor(private snackbar: UtilitiesService) {}

	onFilesSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files || !input.files.length) return;

		this.handleFiles(input.files);
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = true;
	}

	onDragLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = false;
	}

	onDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging = false;

		if (!event.dataTransfer?.files?.length) return;

		this.handleFiles(event.dataTransfer.files);
	}

	copyErrorsToClipboard() {
		const errors = this.getUploadedImagesProblems()
			.map((image) => image.fileName)
			.join('\n');
		navigator.clipboard.writeText(errors).then(() => {
			this.snackbar.showSnackbar('Copied!', SnackbarType.Success);
		});
	}

	handleFiles(files: FileList) {
		this.loading = true;

		const previews: string[] = [];

		Array.from(files).forEach((file: File) => {
			const reader = new FileReader();

			reader.onload = () => {
				previews.push(reader.result as string);

				if (previews.length === files.length) {
					this.uploadedImages = previews.map(
						(preview: string, index: number) =>
							({
								blob: preview,
								file: files[index],
								fileName: files[index].name,
								valid: this.checkImage(files[index].name)
							}) as Selectable<UploadedImageShot>
					);

					this.loading = false;
				}
			};

			reader.readAsDataURL(file);
		});
	}

	getExampleEstimatedCodes() {
		return this.data.estimatedCodes.slice(0, 3);
	}

	checkImage(filename: string) {
		const normalize = (value: string) =>
			value
				.trim()
				.toLowerCase()
				.replace(/\.jpeg$/i, '.jpg');

		const normalizedFilename = normalize(filename);

		return this.data.estimatedCodes.some((code: string) => normalize(code) === normalizedFilename);
	}

	getUploadedImagesProblems() {
		return this.uploadedImages.filter((image) => !image.valid);
	}

	getUploadedImagesOk() {
		return this.uploadedImages.filter((image) => image.valid);
	}

	close() {
		this.#dialogRef.close();
	}

	confirm() {
		this.#dialogRef.close(this.getUploadedImagesOk());
	}
}
